import {
  createSeedData,
  STORAGE_KEY,
  type DbInsert,
  type DbRow,
  type DbUpdate,
  type LocalDb,
  type TableName,
  IDS,
} from "./seed";

export { STORAGE_KEY } from "./seed";

const AUTH_SESSION_KEY = "jivandhara-auth-session";
export const DEMO_ADMIN_KEY = "jivandhara-demo-admin";
const STORAGE_PREFIX = "jivandhara-storage-";

type Filter =
  | { kind: "eq"; field: string; value: unknown }
  | { kind: "neq"; field: string; value: unknown }
  | { kind: "is"; field: string; value: null }
  | { kind: "ilike"; field: string; value: string }
  | { kind: "gte"; field: string; value: unknown }
  | { kind: "in"; field: string; values: unknown[] };

type JoinSpec = {
  alias: string;
  table: string;
  columns: string[];
};

type QueryResult<T = unknown> = {
  data: T;
  error: { message: string; code?: string } | null;
  count?: number | null;
};

type AuthUser = {
  id: string;
  email?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
};

type AuthSession = {
  access_token: string;
  user: AuthUser;
};

type AuthChangeCallback = (
  event: string,
  session: AuthSession | null
) => void;

function uuid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

function loadDb(): Record<string, DbRow[]> {
  if (!hasStorage()) {return { ...createSeedData() };}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seed = createSeedData();
      saveDb(seed);
      return { ...seed };
    }
    const parsed = JSON.parse(raw) as Record<string, DbRow[]>;
    const seed = createSeedData();
    for (const table of Object.keys(seed) as TableName[]) {
      if (!parsed[table]?.length) {parsed[table] = seed[table];}
    }
    return parsed;
  } catch {
    const seed = createSeedData();
    saveDb(seed);
    return { ...seed };
  }
}

function saveDb(db: Record<string, DbRow[]>): void {
  if (!hasStorage()) {return;}
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
}

function tableRows(db: Record<string, DbRow[]>, table: string): DbRow[] {
  if (!db[table]) {db[table] = [];}
  return db[table];
}

function compareValues(a: unknown, b: unknown): number {
  if (
    (a === null || a === undefined) &&
    (b === null || b === undefined)
  ) {
    return 0;
  }
  if (a === null || a === undefined) {return -1;}
  if (b === null || b === undefined) {return 1;}
  if (typeof a === "number" && typeof b === "number") {return a - b;}
  return String(a).localeCompare(String(b));
}

function matchFilter(row: DbRow, filter: Filter): boolean {
  const val = row[filter.field];
  switch (filter.kind) {
    case "eq":
      return val === filter.value;
    case "neq":
      return val !== filter.value;
    case "is":
      return val === null || val === undefined;
    case "ilike": {
      const hay = String(val ?? "").toLowerCase();
      const needle = String(filter.value ?? "")
        .replace(/%/g, "")
        .toLowerCase();
      return hay.includes(needle);
    }
    case "gte":
      return compareValues(val, filter.value) >= 0;
    case "in":
      return filter.values.includes(val);
    default:
      return true;
  }
}

function parseSelect(select: string): {
  columns: string[] | "*";
  joins: JoinSpec[];
} {
  const normalized = select.replace(/\s+/g, " ").trim();
  const joins: JoinSpec[] = [];
  const topLevel: string[] = [];
  let current = "";
  let depth = 0;

  for (const ch of normalized) {
    if (ch === "(") {depth++;}
    if (ch === ")") {depth--;}
    if (ch === "," && depth === 0) {
      if (current.trim()) {topLevel.push(current.trim());}
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) {topLevel.push(current.trim());}

  const columns: string[] = [];
  for (const part of topLevel) {
    const joinMatch = part.match(/^(\w+):(\w+)\s*\(([^)]*)\)$/);
    if (joinMatch) {
      joins.push({
        alias: joinMatch[1],
        table: joinMatch[2],
        columns: joinMatch[3]
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
      });
    } else {
      columns.push(part);
    }
  }

  return {
    columns: columns.length === 1 && columns[0] === "*" ? "*" : columns,
    joins,
  };
}

const JOIN_FK: Record<string, Record<string, { fk: string; reverse?: boolean }>> = {
  donations: {
    donor: { fk: "donor_id" },
    receipt: { fk: "donation_id", reverse: true },
  },
};

function resolveJoin(
  db: Record<string, DbRow[]>,
  parentTable: string,
  parentRow: DbRow,
  join: JoinSpec
): DbRow | DbRow[] | null {
  const mapping = JOIN_FK[parentTable]?.[join.alias];
  const related = tableRows(db, join.table);

  if (mapping?.reverse) {
    const matches = related.filter((r) => r[mapping.fk] === parentRow.id);
    return matches.map((r) => projectColumns(r, join.columns));
  }

  const fk = mapping?.fk ?? `${join.alias}_id`;
  const fkVal = parentRow[fk];
  if (fkVal === null || fkVal === undefined) {return null;}
  const match = related.find((r) => r.id === fkVal);
  return match ? projectColumns(match, join.columns) : null;
}

function projectColumns(row: DbRow, columns: string[]): DbRow {
  if (!columns.length) {return { ...row };}
  const out: DbRow = { id: row.id };
  for (const col of columns) {
    out[col] = row[col];
  }
  return out;
}

function projectRow(
  row: DbRow,
  columns: string[] | "*",
  joins: JoinSpec[],
  db: Record<string, DbRow[]>,
  parentTable: string
): DbRow {
  let out: DbRow;
  if (columns === "*") {
    out = { ...row };
  } else {
    out = { id: row.id } as DbRow;
    for (const col of columns) {
      out[col] = row[col];
    }
    if (!columns.includes("id") && row.id) {out.id = row.id;}
    if (!columns.includes("created_at") && row.created_at) {
      out.created_at = row.created_at;
    }
  }
  for (const join of joins) {
    out[join.alias] = resolveJoin(db, parentTable, row, join);
  }
  return out;
}

function fyStart(d = new Date()): Date {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return new Date(y, 3, 1);
}

function getAdminDashboardSummary(db: Record<string, DbRow[]>): Record<string, unknown> {
  const count = (table: string) => tableRows(db, table).length;
  const donations = tableRows(db, "donations")
    .slice()
    .sort((a, b) =>
      String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""))
    )
    .slice(0, 14)
    .map((d) => ({
      amount: d.amount,
      created_at: d.created_at,
      donation_date: d.donation_date,
      payment_mode: d.payment_mode,
      payment_status: d.payment_status,
      category: d.category,
      donor_id: d.donor_id,
    }));

  const donorsById = Object.fromEntries(
    tableRows(db, "donors").map((d) => [d.id, d])
  );
  const recentDonors = tableRows(db, "donations")
    .slice()
    .sort((a, b) =>
      String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""))
    )
    .slice(0, 5)
    .map((d) => ({
      name: String(donorsById[String(d.donor_id ?? "")]?.full_name ?? "Anonymous"),
      amount: Number(d.amount ?? 0),
      date: String(d.created_at ?? d.donation_date ?? ""),
    }));

  return {
    counts: {
      residents: count("residents"),
      donors: count("donors"),
      donations: count("donations"),
      adoption_sponsorships: count("adoption_sponsorships"),
      tiffin_requests: count("tiffin_requests"),
      feedback: count("feedback"),
      contact_inquiries: count("contact_inquiries"),
      visitors: count("visitors"),
      events: count("events"),
      posts: count("posts"),
      punya_tithi: count("punya_tithi"),
      trustees: count("trustees"),
      committee_members: count("committee_members"),
      gallery: count("gallery"),
    },
    donations,
    recentDonors,
  };
}

function getDonorLifetimeStats(
  db: Record<string, DbRow[]>,
  donorId: string
): Record<string, unknown> {
  const donations = tableRows(db, "donations").filter(
    (d) =>
      d.donor_id === donorId &&
      (d.payment_status ?? "success") === "success"
  );
  const lifetime_total = donations.reduce(
    (sum, d) => sum + Number(d.amount ?? 0),
    0
  );
  const fy0 = fyStart();
  const fy_total = donations
    .filter((d) => new Date(String(d.donation_date ?? d.created_at)) >= fy0)
    .reduce((sum, d) => sum + Number(d.amount ?? 0), 0);
  const sorted = donations.slice().sort((a, b) =>
    String(b.donation_date ?? b.created_at ?? "").localeCompare(
      String(a.donation_date ?? a.created_at ?? "")
    )
  );
  return {
    lifetime_total,
    lifetime_count: donations.length,
    fy_total,
    last_donation_at: sorted[0]?.donation_date ?? sorted[0]?.created_at ?? null,
  };
}

function loadAuthSession(): AuthSession | null {
  if (!hasStorage()) {return null;}
  try {
    const raw = window.localStorage.getItem(AUTH_SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthSession) : null;
  } catch {
    return null;
  }
}

function saveAuthSession(session: AuthSession | null): void {
  if (!hasStorage()) {return;}
  if (session) {
    window.localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
  } else {
    window.localStorage.removeItem(AUTH_SESSION_KEY);
  }
}

function demoAdminSession(): AuthSession {
  return {
    access_token: "demo-admin-token",
    user: {
      id: IDS.adminUser,
      email: "admin@jivandhara.org",
      app_metadata: { role: "super_admin" },
      user_metadata: { full_name: "Demo Admin" },
    },
  };
}

function effectiveSession(): AuthSession | null {
  if (hasStorage() && window.localStorage.getItem(DEMO_ADMIN_KEY) === "true") {
    return demoAdminSession();
  }
  return loadAuthSession();
}

class QueryBuilder<T = DbRow[]> implements PromiseLike<QueryResult<T>> {
  private op: "select" | "insert" | "update" | "delete" = "select";
  private filters: Filter[] = [];
  private selectStr = "*";
  private selectOptions?: { count?: "exact"; head?: boolean };
  private orderSpec?: { field: string; ascending: boolean };
  private limitN?: number;
  private insertPayload?: DbInsert | DbInsert[];
  private updatePayload?: DbUpdate;
  private returnSelect = false;
  private singleMode: "maybe" | "single" | null = null;

  constructor(private table: string) {}

  select(
    columns = "*",
    options?: { count?: "exact"; head?: boolean }
  ): this {
    if (this.op === "insert" || this.op === "update") {
      this.returnSelect = true;
      this.selectStr = columns;
      this.selectOptions = options;
      return this;
    }
    this.op = "select";
    this.selectStr = columns;
    this.selectOptions = options;
    return this;
  }

  eq(field: string, value: unknown): this {
    this.filters.push({ kind: "eq", field, value });
    return this;
  }

  neq(field: string, value: unknown): this {
    this.filters.push({ kind: "neq", field, value });
    return this;
  }

  is(field: string, value: null): this {
    this.filters.push({ kind: "is", field, value });
    return this;
  }

  ilike(field: string, value: string): this {
    this.filters.push({ kind: "ilike", field, value });
    return this;
  }

  gte(field: string, value: unknown): this {
    this.filters.push({ kind: "gte", field, value });
    return this;
  }

  in(field: string, values: unknown[]): this {
    this.filters.push({ kind: "in", field, values });
    return this;
  }

  order(field: string, opts?: { ascending?: boolean }): this {
    this.orderSpec = { field, ascending: opts?.ascending ?? true };
    return this;
  }

  limit(n: number): this {
    this.limitN = n;
    return this;
  }

  maybeSingle(): QueryBuilder<DbRow | null> {
    this.singleMode = "maybe";
    this.limitN = 1;
    return this as unknown as QueryBuilder<DbRow | null>;
  }

  single(): QueryBuilder<DbRow | null> {
    this.singleMode = "single";
    this.limitN = 1;
    return this as unknown as QueryBuilder<DbRow | null>;
  }

  insert(payload: DbInsert | DbInsert[]): this {
    this.op = "insert";
    this.insertPayload = payload;
    return this;
  }

  update(payload: DbUpdate): this {
    this.op = "update";
    this.updatePayload = payload;
    return this;
  }

  delete(): this {
    this.op = "delete";
    return this;
  }

  then<TResult1 = QueryResult<T>, TResult2 = never>(
    onfulfilled?:
      | ((value: QueryResult<T>) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }

  private async execute(): Promise<QueryResult<T>> {
    try {
      const db = loadDb();
      const rows = tableRows(db, this.table);

      if (this.op === "insert") {
        return this.executeInsert(db, rows) as QueryResult<T>;
      }
      if (this.op === "update") {
        return this.executeUpdate(db, rows) as QueryResult<T>;
      }
      if (this.op === "delete") {
        return this.executeDelete(db, rows) as QueryResult<T>;
      }
      return this.executeSelect(db, rows) as QueryResult<T>;
    } catch (err) {
      return {
        data: null as T,
        error: {
          message: err instanceof Error ? err.message : "Unknown error",
        },
      };
    }
  }

  private executeInsert(
    db: Record<string, DbRow[]>,
    rows: DbRow[]
  ): QueryResult<DbRow | DbRow[] | null> {
    const items = Array.isArray(this.insertPayload)
      ? this.insertPayload
      : [this.insertPayload!];
    const inserted: DbRow[] = items.map((item) => {
      const now = new Date().toISOString();
      const row: DbRow = {
        ...item,
        id: String(item.id ?? uuid()),
        created_at: String(item.created_at ?? now),
      };
      if (this.table === "payment_settings" && !row.updated_at) {
        row.updated_at = now;
      }
      if (this.table === "residents" && !row.updated_at) {
        row.updated_at = now;
      }
      rows.push(row);
      return row;
    });
    saveDb(db);

    if (!this.returnSelect) {
      return { data: null, error: null };
    }

    const { columns, joins } = parseSelect(this.selectStr);
    const projected = inserted.map((r) =>
      projectRow(r, columns, joins, db, this.table)
    );

    if (this.singleMode === "single") {
      return { data: projected[0] ?? null, error: null };
    }
    if (this.singleMode === "maybe") {
      return { data: projected[0] ?? null, error: null };
    }
    return { data: projected, error: null };
  }

  private executeUpdate(
    db: Record<string, DbRow[]>,
    rows: DbRow[]
  ): QueryResult<DbRow | DbRow[] | null> {
    const updated: DbRow[] = [];
    for (let i = 0; i < rows.length; i++) {
      if (this.filters.every((f) => matchFilter(rows[i], f))) {
        rows[i] = { ...rows[i], ...this.updatePayload };
        updated.push(rows[i]);
      }
    }
    saveDb(db);

    if (!this.returnSelect) {
      return { data: null, error: null };
    }

    const { columns, joins } = parseSelect(this.selectStr);
    const projected = updated.map((r) =>
      projectRow(r, columns, joins, db, this.table)
    );
    if (this.singleMode) {
      return { data: projected[0] ?? null, error: null };
    }
    return { data: projected, error: null };
  }

  private executeDelete(
    db: Record<string, DbRow[]>,
    rows: DbRow[]
  ): QueryResult<null> {
    const keep = rows.filter(
      (row) => !this.filters.every((f) => matchFilter(row, f))
    );
    db[this.table] = keep;
    saveDb(db);
    return { data: null, error: null };
  }

  private executeSelect(
    db: Record<string, DbRow[]>,
    rows: DbRow[]
  ): QueryResult<DbRow | DbRow[] | null> {
    if (this.selectOptions?.count === "exact" && this.selectOptions?.head) {
      const count = rows.filter((row) =>
        this.filters.every((f) => matchFilter(row, f))
      ).length;
      return { data: null, count, error: null };
    }

    let filtered = rows.filter((row) =>
      this.filters.every((f) => matchFilter(row, f))
    );

    if (this.orderSpec) {
      const { field, ascending } = this.orderSpec;
      filtered = filtered.slice().sort((a, b) => {
        const cmp = compareValues(a[field], b[field]);
        return ascending ? cmp : -cmp;
      });
    }

    if (this.limitN !== null && this.limitN !== undefined) {
      filtered = filtered.slice(0, this.limitN);
    }

    const { columns, joins } = parseSelect(this.selectStr);
    const projected = filtered.map((r) =>
      projectRow(r, columns, joins, db, this.table)
    );

    if (this.singleMode === "single") {
      if (projected.length === 0) {
        return {
          data: null,
          error: { message: "No rows found", code: "PGRST116" },
        };
      }
      if (projected.length > 1) {
        return {
          data: null,
          error: { message: "Multiple rows found", code: "PGRST116" },
        };
      }
      return { data: projected[0], error: null };
    }

    if (this.singleMode === "maybe") {
      return { data: projected[0] ?? null, error: null };
    }

    return { data: projected, error: null };
  }
}

class RpcBuilder implements PromiseLike<QueryResult<unknown>> {
  constructor(
    private name: string,
    private args: Record<string, unknown> = {}
  ) {}

  then<TResult1 = QueryResult<unknown>, TResult2 = never>(
    onfulfilled?:
      | ((value: QueryResult<unknown>) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }

  private async execute(): Promise<QueryResult<unknown>> {
    const db = loadDb();
    if (this.name === "get_admin_dashboard_summary") {
      return { data: getAdminDashboardSummary(db), error: null };
    }
    if (this.name === "donor_lifetime_stats") {
      const donorId = String(this.args._donor_id ?? "");
      return { data: getDonorLifetimeStats(db, donorId), error: null };
    }
    return {
      data: null,
      error: { message: `Unknown RPC: ${this.name}` },
    };
  }
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

class StorageBucket {
  constructor(private bucket: string) {}

  async upload(
    path: string,
    file: File,
    _opts?: { cacheControl?: string; upsert?: boolean }
  ): Promise<{ data: { path: string } | null; error: { message: string } | null }> {
    try {
      const dataUrl = await fileToDataUrl(file);
      if (hasStorage()) {
        window.localStorage.setItem(
          `${STORAGE_PREFIX}${this.bucket}-${path}`,
          dataUrl
        );
      }
      return { data: { path }, error: null };
    } catch (err) {
      return {
        data: null,
        error: {
          message: err instanceof Error ? err.message : "Upload failed",
        },
      };
    }
  }

  getPublicUrl(path: string): { data: { publicUrl: string } } {
    const stored = hasStorage()
      ? window.localStorage.getItem(`${STORAGE_PREFIX}${this.bucket}-${path}`)
      : null;
    const url = stored ?? "";
    return { data: { publicUrl: url } };
  }
}

const authListeners = new Set<AuthChangeCallback>();

function notifyAuthListeners(event: string, session: AuthSession | null): void {
  for (const cb of authListeners) {
    try {
      cb(event, session);
    } catch {
      /* ignore listener errors */
    }
  }
}

function createAuthApi() {
  return {
    async getSession(): Promise<{
      data: { session: AuthSession | null };
      error: null;
    }> {
      return { data: { session: effectiveSession() }, error: null };
    },

    async getUser(): Promise<{
      data: { user: AuthUser | null };
      error: null;
    }> {
      const session = effectiveSession();
      return { data: { user: session?.user ?? null }, error: null };
    },

    async signOut(): Promise<{ error: null }> {
      if (hasStorage()) {
        window.localStorage.removeItem(DEMO_ADMIN_KEY);
      }
      saveAuthSession(null);
      notifyAuthListeners("SIGNED_OUT", null);
      return { error: null };
    },

    async signInWithPassword(credentials: {
      email: string;
      password: string;
    }): Promise<{
      data: { session: AuthSession | null; user: AuthUser | null };
      error: { message: string } | null;
    }> {
      const email = credentials.email.trim().toLowerCase();
      const password = credentials.password;

      if (
        (email === "admin" || email === "admin@jivandhara.org") &&
        password === "admin"
      ) {
        if (hasStorage()) {
          window.localStorage.setItem(DEMO_ADMIN_KEY, "true");
        }
        const session = demoAdminSession();
        notifyAuthListeners("SIGNED_IN", session);
        return { data: { session, user: session.user }, error: null };
      }

      const db = loadDb();
      const profile = tableRows(db, "profiles").find(
        (p) => String(p.email ?? "").toLowerCase() === email
      );
      if (!profile || password.length < 4) {
        return {
          data: { session: null, user: null },
          error: { message: "Invalid login credentials" },
        };
      }

      if (hasStorage()) {
        window.localStorage.removeItem(DEMO_ADMIN_KEY);
      }

      const session: AuthSession = {
        access_token: uuid(),
        user: {
          id: String(profile.id),
          email: String(profile.email ?? email),
          user_metadata: { full_name: profile.full_name },
        },
      };
      saveAuthSession(session);
      notifyAuthListeners("SIGNED_IN", session);
      return { data: { session, user: session.user }, error: null };
    },

    onAuthStateChange(callback: AuthChangeCallback): {
      data: { subscription: { unsubscribe: () => void } };
    } {
      authListeners.add(callback);
      queueMicrotask(() => callback("INITIAL_SESSION", effectiveSession()));
      return {
        data: {
          subscription: {
            unsubscribe: () => {
              authListeners.delete(callback);
            },
          },
        },
      };
    },
  };
}

export function createLocalClient() {
  const auth = createAuthApi();

  return {
    auth,
    from(table: string) {
      return new QueryBuilder(table);
    },
    rpc(name: string, args?: Record<string, unknown>) {
      return new RpcBuilder(name, args);
    },
    storage: {
      from(bucket: string) {
        return new StorageBucket(bucket);
      },
    },
    /** Reset database to seed data (useful for development). */
    resetDb(): LocalDb {
      const seed = createSeedData();
      saveDb(seed);
      return seed;
    },
  };
}

export type LocalSupabaseClient = ReturnType<typeof createLocalClient>;
